import { useState } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { upsert } from '../../../Redux/ensemblesSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import { ApiError } from '../../../Models/ApiModel';
import type { EnsembleModel, EnsembleSlotModel } from '../../../Models/EnsembleModel';
import type { Weekday } from '../../../Models/enums';
import { EnsembleService } from '../../../Services/EnsembleService';

const WEEKDAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * The group's recurring weekly hour, edited where the rest of the group is.
 *
 * It does the same two jobs a student's slot does: a meeting booked without a
 * time lands at the right hour, and the hour is held against everything else,
 * so the API refuses a private lesson that would collide with a rehearsal and
 * the other way round.
 *
 * Saved on the spot rather than with the form, for the same reason slots are:
 * they have their own endpoints and the server is the authority on the list.
 */
export function EnsembleSlotEditor({ ensemble }: { ensemble: EnsembleModel }) {
  const { tr } = useTranslation();
  const dispatch = useAppDispatch();
  const [weekday, setWeekday] = useState<Weekday>(0);
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(String(ensemble.durationMinutes || 60));
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);

  function replaceSlots(slots: EnsembleSlotModel[]) {
    dispatch(upsert({ ...ensemble, slots }));
  }

  async function add() {
    if (!time) return;
    setBusy(true);
    setConflict(false);
    try {
      const slot = await EnsembleService.addSlot(ensemble.name, {
        weekday,
        time,
        durationMinutes: Number(duration) || 60
      });
      replaceSlots([...ensemble.slots, slot]);
      setTime('');
    } catch (failure) {
      // An hour already held by a student or another group is an expected
      // outcome the tutor has to react to, not a hiccup worth a generic error.
      if (failure instanceof ApiError && failure.code === 'SLOT_CONFLICT') setConflict(true);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      await EnsembleService.removeSlot(id);
      replaceSlots(ensemble.slots.filter(slot => slot.id !== id));
    } catch {
      // The hour stays listed because it is still stored.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="editor-section is-wide">
      <h3>{tr('ensembleWeeklyHour')}</h3>

      {ensemble.slots.length > 0 && (
        <ul className="editor-chip-list">
          {ensemble.slots.map(slot => (
            <li key={slot.id}>
              <bdi>{tr(WEEKDAY_KEYS[slot.weekday])} {slot.time} · {slot.durationMinutes}′</bdi>
              <button type="button" onClick={() => void remove(slot.id)} disabled={busy} aria-label={tr('remove')}>×</button>
            </li>
          ))}
        </ul>
      )}

      {conflict && <p className="editor-section-error" role="alert">{tr('slotConflict')}</p>}

      <div className="editor-row">
        <label className="field">
          <span>{tr('weekday')}</span>
          <select
            className="text-input"
            value={weekday}
            onChange={e => { setWeekday(Number(e.target.value) as Weekday); setConflict(false); }}
          >
            {WEEKDAYS.map(day => <option key={day} value={day}>{tr(WEEKDAY_KEYS[day])}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{tr('time')}</span>
          <input
            className="text-input"
            type="time"
            value={time}
            onChange={e => { setTime(e.target.value); setConflict(false); }}
          />
        </label>
        <label className="field">
          <span>{tr('durationMinutes')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={duration}
            onChange={e => { setDuration(e.target.value.replace(/\D/g, '')); setConflict(false); }}
          />
        </label>
        {/* type=button, or it submits the form it is nested beside. */}
        <button type="button" className="ghost-button" onClick={() => void add()} disabled={busy || !time}>
          {tr('add')}
        </button>
      </div>
    </section>
  );
}
